import { useCallback, useEffect, useRef, useState } from "react";
import CustomerLineFilterToolbox from "@/components/CustomerLineFilterToolbox.jsx";
import AdminCustomerLineLabel from "@/components/AdminCustomerLineLabel.jsx";
import Price from "@/components/Price.jsx";
import { searchAdminCarts } from "@/api/admin.api.js";
import { getFileUrl } from "@/api/public.api.js";
import { EMPTY_CUSTOMER_LINE_FILTERS } from "@/utils/customerLineFilters.js";
import "@/components/product-filter.css";

function formatAddedDate(value) {
    if (!value) {
        return "—";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }
    return date.toLocaleString("fr-FR");
}

function productImageUrl(photoId) {
    return photoId ? getFileUrl(photoId) : null;
}

export default function AdminCartsSection({ setMessage, setError }) {
    const [draftFilters, setDraftFilters] = useState(EMPTY_CUSTOMER_LINE_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(EMPTY_CUSTOMER_LINE_FILTERS);
    const [lines, setLines] = useState([]);
    const [loading, setLoading] = useState(false);
    const loadRequestIdRef = useRef(0);

    const load = useCallback(async (filters) => {
        const requestId = ++loadRequestIdRef.current;
        setLoading(true);
        setError("");
        setMessage("");
        try {
            const response = await searchAdminCarts({
                customer: filters.customer.trim() || undefined,
                search: filters.search.trim() || undefined,
                dateFrom: filters.dateFrom || undefined,
                dateTo: filters.dateTo || undefined,
                page: 0,
                size: 50
            });
            if (requestId !== loadRequestIdRef.current) {
                return;
            }
            setLines(response.data || []);
        } catch (err) {
            if (requestId !== loadRequestIdRef.current) {
                return;
            }
            setError(err.message);
            setLines([]);
        } finally {
            if (requestId === loadRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [setError, setMessage]);

    useEffect(() => {
        load(appliedFilters);
    }, [load, appliedFilters]);

    const applyFilters = async (nextFilters) => {
        const filtersToApply = nextFilters ?? draftFilters;
        if (nextFilters) {
            setDraftFilters(nextFilters);
        }
        setAppliedFilters({ ...filtersToApply });
    };

    const resetFilters = async () => {
        setDraftFilters(EMPTY_CUSTOMER_LINE_FILTERS);
        setAppliedFilters(EMPTY_CUSTOMER_LINE_FILTERS);
    };

    return (
        <section className="card profile-section profile-section--catalog">
            <div className="catalog-with-filters">
                <CustomerLineFilterToolbox
                    filters={draftFilters}
                    loading={loading}
                    onChange={setDraftFilters}
                    onApply={applyFilters}
                    onReset={resetFilters}
                />

                <div className="catalog-results">
                    <div className="catalog-results-header">
                        <h2>Gérer les paniers</h2>
                        <p className="muted catalog-results-count admin-list-hint">
                            Consultez le contenu des paniers clients. Les articles les plus récemment ajoutés apparaissent en premier.
                        </p>
                    </div>

                    {loading && <p className="muted">Chargement des paniers...</p>}
                    {!loading && lines.length === 0 && (
                        <p className="muted">Aucun article de panier ne correspond à votre recherche.</p>
                    )}

                    {!loading && lines.length > 0 && (
                        <div className="admin-table purchase-list">
                            {lines.map((line) => (
                                <div key={line.id} className="admin-row admin-list-row purchase-list-row admin-cart-line-row">
                                    <div className="admin-list-row-main purchase-list-row-main purchase-list-row-main--static">
                                        <AdminCustomerLineLabel
                                            displayName={line.customerDisplayName}
                                            username={line.username}
                                        />
                                        <div className="purchase-list-row-top">
                                            {productImageUrl(line.photoId) ? (
                                                <img
                                                    src={productImageUrl(line.photoId)}
                                                    alt=""
                                                    className="purchase-row-thumb"
                                                />
                                            ) : (
                                                <div className="purchase-row-thumb purchase-row-thumb--empty" aria-hidden="true" />
                                            )}
                                            <div className="purchase-list-row-heading">
                                                <strong>{line.title}</strong>
                                                {line.category && <p className="muted">{line.category}</p>}
                                                <p><Price value={line.unitPrice} /></p>
                                            </div>
                                            <div className="admin-customer-cart-line-meta">
                                                <p>
                                                    Qté <strong>{line.quantity}</strong>
                                                </p>
                                                <p>
                                                    <strong><Price unitPrice={line.unitPrice} quantity={line.quantity} /></strong>
                                                </p>
                                            </div>
                                        </div>
                                        <p className="muted purchase-row-date">
                                            Ajouté le {formatAddedDate(line.addedAt)}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
